import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { Input } from '@/components/ui/Input';
import { searchSettings } from '@/config/settingsRegistry';
import { cn } from '@/utils/cn';
import { useCanViewSettingsMaster, useModuleScopedSettingsMasters } from './settingsAccess';

type Props = {
  compact?: boolean;
  className?: string;
};

export function SettingsSearch({ compact = false, className }: Props) {
  const [query, setQuery] = useState('');
  const canView = useCanViewSettingsMaster();
  const scopedMasters = useModuleScopedSettingsMasters();
  const scopedRoutes = useMemo(() => new Set(scopedMasters.map((master) => master.route)), [scopedMasters]);
  const results = useMemo(
    () => searchSettings(query).filter((item) => canView(item) && scopedRoutes.has(item.route)).slice(0, 8),
    [canView, query, scopedRoutes],
  );

  const showResults = query.trim().length > 0;

  if (compact) {
    return (
      <div className={cn('relative w-full min-w-[14rem] max-w-xs', className)}>
        <SearchIcon
          sx={{ fontSize: 18 }}
          className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={query}
          placeholder="Search settings..."
          aria-label="Search settings"
          onChange={(event) => setQuery(event.target.value)}
          className={cn(
            'w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm',
            'placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            'dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500',
          )}
        />
        {showResults && (
          <div className="absolute right-0 top-[calc(100%+0.5rem)] z-20 w-[min(100vw-3rem,22rem)] rounded-xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {results.length > 0 ? (
              <ul className="grid gap-1">
                {results.map((item) => (
                  <li key={item.route}>
                    <Link
                      to={item.route}
                      className="block rounded-lg px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                      onClick={() => setQuery('')}
                    >
                      <div className="text-sm font-medium text-slate-900 dark:text-white">{item.label}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{item.description}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No matching settings found.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('card p-4', className)}>
      <Input
        label="Search settings"
        value={query}
        placeholder="Search within this module's settings..."
        onChange={(event) => setQuery(event.target.value)}
      />
      {showResults ? (
        <div className="mt-3 grid gap-2">
          {results.length > 0 ? (
            results.map((item) => (
              <Link
                key={item.route}
                to={item.route}
                className="rounded-lg border border-slate-100 px-3 py-2 text-sm transition hover:border-primary-200 hover:bg-primary-50/40 dark:border-slate-800 dark:hover:border-primary-900 dark:hover:bg-primary-950/20"
              >
                <div className="font-medium text-slate-900 dark:text-white">{item.label}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{item.description}</div>
              </Link>
            ))
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <SearchIcon fontSize="small" /> No matching settings found.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
